import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { getCurrencyByLocation, formatPrice } from '../utils/currency';
import { FaCreditCard, FaFileInvoice, FaUniversity, FaTruck, FaMoneyBill, FaCalendarAlt, FaBuilding, FaUpload, FaCheck } from 'react-icons/fa';

export default function Checkout() {
  const { t, i18n } = useTranslation();
  const { API, cart, cartTotal, clearCart, user } = useContext(AppContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');
  
  const [existingOrder, setExistingOrder] = useState(null);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    company: user?.company || '',
    address: user?.address || '',
    siret: '',
    paymentMethod: 'bank_transfer',
    paymentTerms: '0', // 0 = immediate, 30, 45 days
    purchaseOrderNumber: '', // Numéro de bon de commande client
    purchaseOrderFile: null, // URL du fichier BC uploadé
    purchaseOrderFileName: null // Nom du fichier BC
  });
  const [loading, setLoading] = useState(false);
  const [orderSubmitted, setOrderSubmitted] = useState(false);

  // Fetch existing order if order_id is provided (from quote validation)
  useEffect(() => {
    if (orderId) {
      fetchOrder();
    }
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}`);
      setExistingOrder(response.data);
      // Pre-fill form with order data
      setFormData(prev => ({
        ...prev,
        name: response.data.user_name || prev.name,
        email: response.data.user_email || prev.email,
        address: response.data.delivery_address || prev.address
      }));
    } catch (error) {
      console.error('Error fetching order:', error);
      toast.error('Commande non trouvée');
    }
  };

  const deliveryCost = existingOrder ? (existingOrder.delivery_cost || 0) : 15;
  const itemsTotal = existingOrder ? existingOrder.total : cartTotal;
  const total = existingOrder ? existingOrder.total + deliveryCost : cartTotal + deliveryCost;

  // Calculate due date based on payment terms
  const getDueDate = (days) => {
    const date = new Date();
    date.setDate(date.getDate() + parseInt(days));
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate purchase order number if payment terms selected
    if (formData.paymentMethod === 'purchase_order' && !formData.purchaseOrderNumber) {
      toast.error(i18n.language === 'fr' ? 'Veuillez entrer votre numéro de bon de commande' : 'Please enter your purchase order number');
      return;
    }
    
    setLoading(true);

    try {
      // Calculate payment due date
      const paymentDueDate = new Date();
      paymentDueDate.setDate(paymentDueDate.getDate() + parseInt(formData.paymentTerms || 0));

      if (existingOrder) {
        // Update existing order with payment info
        await axios.put(`${API}/orders/${existingOrder.id}`, {
          payment_method: formData.paymentMethod,
          payment_terms: parseInt(formData.paymentTerms),
          payment_due_date: paymentDueDate.toISOString(),
          purchase_order_number: formData.purchaseOrderNumber || null,
          purchase_order_file: formData.purchaseOrderFile || null,
          delivery_address: formData.address,
          delivery_cost: deliveryCost,
          company: formData.company,
          siret: formData.siret,
          status: formData.paymentMethod === 'purchase_order' ? 'confirmed' : 'pending_payment',
          payment_status: formData.paymentMethod === 'purchase_order' ? 'invoiced' : 'pending'
        });
        
        toast.success(i18n.language === 'fr' ? 'Commande confirmée !' : 'Order confirmed!');
        navigate(`/order-confirmation/${existingOrder.id}`);
      } else {
        // Create new order from cart
        const orderData = {
          user_id: user?.id || null,
          user_email: formData.email,
          user_name: formData.name,
          company: formData.company,
          siret: formData.siret,
          items: cart.map(item => ({
            product_id: item.product.id,
            name: i18n.language === 'fr' ? item.product.name_fr : item.product.name_en,
            type: item.type,
            quantity: item.quantity,
            price: item.type === 'new' ? item.product.price_new :
                   item.type === 'used' ? item.product.price_used :
                   item.product.price_rental
          })),
          total: total,
          payment_method: formData.paymentMethod,
          payment_terms: parseInt(formData.paymentTerms),
          payment_due_date: paymentDueDate.toISOString(),
          purchase_order_number: formData.purchaseOrderNumber || null,
          delivery_address: formData.address,
          delivery_cost: deliveryCost,
          status: formData.paymentMethod === 'purchase_order' ? 'confirmed' : 'pending',
          payment_status: formData.paymentMethod === 'purchase_order' ? 'invoiced' : 'pending'
        };

        const response = await axios.post(`${API}/orders`, orderData);
        toast.success(t('checkout.orderSuccess'));
        setOrderSubmitted(true);
        clearCart();
        navigate(`/order-confirmation/${response.data.id}`);
      }
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur lors de la commande' : 'Error placing order');
    } finally {
      setLoading(false);
    }
  };

  // If no cart and no existing order, redirect
  if (cart.length === 0 && !orderSubmitted && !orderId && !existingOrder) {
    navigate('/cart');
    return null;
  }

  const paymentMethods = [
    { 
      id: 'purchase_order', 
      label: i18n.language === 'fr' ? 'Bon de Commande (B2B)' : 'Purchase Order (B2B)',
      description: i18n.language === 'fr' ? 'Paiement à 30 ou 45 jours sur facture' : 'Payment within 30 or 45 days on invoice',
      icon: FaFileInvoice,
      recommended: true
    },
    { 
      id: 'bank_transfer', 
      label: i18n.language === 'fr' ? 'Virement bancaire' : 'Bank Transfer',
      description: i18n.language === 'fr' ? 'Paiement immédiat par virement' : 'Immediate payment by transfer',
      icon: FaUniversity 
    },
    { 
      id: 'card', 
      label: i18n.language === 'fr' ? 'Carte bancaire' : 'Credit Card',
      description: i18n.language === 'fr' ? 'Paiement sécurisé en ligne' : 'Secure online payment',
      icon: FaCreditCard 
    },
    { 
      id: 'cash', 
      label: i18n.language === 'fr' ? 'Espèces' : 'Cash',
      description: i18n.language === 'fr' ? 'Paiement à la livraison' : 'Payment on delivery',
      icon: FaMoneyBill 
    }
  ];

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="checkout-page">
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-2 text-[#0A2540]">
          {existingOrder 
            ? (i18n.language === 'fr' ? '💳 Finaliser la commande' : '💳 Complete Order')
            : t('checkout.title')
          }
        </h1>
        {existingOrder && (
          <p className="text-gray-600 mb-6">
            {i18n.language === 'fr' 
              ? `Commande #${existingOrder.id.substring(0, 8).toUpperCase()} - Choisissez votre mode de paiement`
              : `Order #${existingOrder.id.substring(0, 8).toUpperCase()} - Choose your payment method`
            }
          </p>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Billing Info */}
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-xl font-bold mb-4 text-[#0A2540] flex items-center gap-2">
                    <FaBuilding className="text-[#635BFF]" />
                    {i18n.language === 'fr' ? 'Informations de facturation' : 'Billing Information'}
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="company">{i18n.language === 'fr' ? 'Entreprise' : 'Company'}</Label>
                      <Input
                        id="company"
                        value={formData.company}
                        onChange={(e) => setFormData({...formData, company: e.target.value})}
                        placeholder="Ex: ACME SAS"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="siret">SIRET</Label>
                      <Input
                        id="siret"
                        value={formData.siret}
                        onChange={(e) => setFormData({...formData, siret: e.target.value})}
                        placeholder="123 456 789 00012"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="name">{t('checkout.name')} *</Label>
                      <Input
                        id="name"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                      />
                    </div>

                    <div>
                      <Label htmlFor="email">{t('checkout.email')} *</Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <Label htmlFor="address">{t('checkout.address')} *</Label>
                    <Input
                      id="address"
                      required
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                      placeholder={i18n.language === 'fr' ? 'Adresse de livraison complète' : 'Full delivery address'}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Payment Method */}
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-xl font-bold mb-4 text-[#0A2540] flex items-center gap-2">
                    <FaCreditCard className="text-[#635BFF]" />
                    {i18n.language === 'fr' ? 'Mode de paiement' : 'Payment Method'}
                  </h2>
                  
                  <RadioGroup 
                    value={formData.paymentMethod} 
                    onValueChange={(value) => setFormData({...formData, paymentMethod: value, paymentTerms: value === 'purchase_order' ? '30' : '0'})}
                    className="space-y-3"
                  >
                    {paymentMethods.map(method => {
                      const Icon = method.icon;
                      const isSelected = formData.paymentMethod === method.id;
                      return (
                        <label 
                          key={method.id}
                          htmlFor={method.id}
                          className={`relative flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            isSelected 
                              ? 'border-[#635BFF] bg-[#635BFF]/5' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <RadioGroupItem value={method.id} id={method.id} className="mt-1" />
                          <div className="ml-3 flex-1">
                            <div className="flex items-center gap-2">
                              <Icon className={`${isSelected ? 'text-[#635BFF]' : 'text-gray-400'}`} />
                              <span className="font-medium">
                                {method.label}
                              </span>
                              {method.recommended && (
                                <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-medium">
                                  {i18n.language === 'fr' ? 'Recommandé B2B' : 'Recommended B2B'}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 mt-1">{method.description}</p>
                          </div>
                        </label>
                      );
                    })}
                  </RadioGroup>

                  {/* Purchase Order specific fields */}
                  {formData.paymentMethod === 'purchase_order' && (
                    <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <h3 className="font-medium text-blue-900 mb-4 flex items-center gap-2">
                        <FaFileInvoice />
                        {i18n.language === 'fr' ? 'Détails du Bon de Commande' : 'Purchase Order Details'}
                      </h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="purchaseOrderNumber">
                            {i18n.language === 'fr' ? 'N° Bon de Commande *' : 'PO Number *'}
                          </Label>
                          <Input
                            id="purchaseOrderNumber"
                            required={formData.paymentMethod === 'purchase_order'}
                            value={formData.purchaseOrderNumber}
                            onChange={(e) => setFormData({...formData, purchaseOrderNumber: e.target.value})}
                            placeholder="Ex: BC-2025-001234"
                            className="bg-white"
                          />
                        </div>
                        
                        <div>
                          <Label>{i18n.language === 'fr' ? 'Échéance de paiement' : 'Payment Terms'}</Label>
                          <Select 
                            value={formData.paymentTerms} 
                            onValueChange={(value) => setFormData({...formData, paymentTerms: value})}
                          >
                            <SelectTrigger className="bg-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="30">30 {i18n.language === 'fr' ? 'jours' : 'days'}</SelectItem>
                              <SelectItem value="45">45 {i18n.language === 'fr' ? 'jours' : 'days'}</SelectItem>
                              <SelectItem value="60">60 {i18n.language === 'fr' ? 'jours' : 'days'}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      {/* Purchase Order File Upload */}
                      <div className="mt-4">
                        <Label htmlFor="poFile">
                          {i18n.language === 'fr' ? '📎 Joindre le Bon de Commande (PDF, Image)' : '📎 Attach Purchase Order (PDF, Image)'}
                        </Label>
                        <div className="mt-2">
                          <input
                            type="file"
                            id="poFile"
                            accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                // Validate file size (max 10MB)
                                if (file.size > 10 * 1024 * 1024) {
                                  toast.error(i18n.language === 'fr' ? 'Fichier trop volumineux (max 10MB)' : 'File too large (max 10MB)');
                                  return;
                                }
                                try {
                                  const uploadData = new FormData();
                                  uploadData.append('file', file);
                                  const response = await axios.post(`${API}/upload-image`, uploadData, {
                                    headers: { 'Content-Type': 'multipart/form-data' }
                                  });
                                  setFormData({...formData, purchaseOrderFile: response.data.url, purchaseOrderFileName: file.name});
                                  toast.success(i18n.language === 'fr' ? 'Bon de commande joint !' : 'Purchase order attached!');
                                } catch (error) {
                                  toast.error(i18n.language === 'fr' ? 'Erreur lors du téléchargement' : 'Upload error');
                                }
                              }
                            }}
                            className="hidden"
                          />
                          <label
                            htmlFor="poFile"
                            className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer transition-all ${
                              formData.purchaseOrderFile 
                                ? 'border-green-400 bg-green-50' 
                                : 'border-gray-300 bg-white hover:border-blue-400 hover:bg-blue-50'
                            }`}
                          >
                            {formData.purchaseOrderFile ? (
                              <>
                                <FaCheck className="text-green-600" />
                                <span className="text-green-700 font-medium">{formData.purchaseOrderFileName || 'Fichier joint'}</span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setFormData({...formData, purchaseOrderFile: null, purchaseOrderFileName: null});
                                  }}
                                  className="ml-2 text-red-500 hover:text-red-700"
                                >
                                  ✕
                                </button>
                              </>
                            ) : (
                              <>
                                <FaUpload className="text-gray-400" />
                                <span className="text-gray-600">
                                  {i18n.language === 'fr' ? 'Cliquez pour joindre votre BC' : 'Click to attach your PO'}
                                </span>
                              </>
                            )}
                          </label>
                          <p className="text-xs text-gray-500 mt-1">
                            {i18n.language === 'fr' ? 'Formats acceptés : PDF, PNG, JPG, DOC (max 10MB)' : 'Accepted formats: PDF, PNG, JPG, DOC (max 10MB)'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="mt-4 p-3 bg-white rounded border flex items-center gap-3">
                        <FaCalendarAlt className="text-blue-600 text-xl" />
                        <div>
                          <p className="text-sm text-gray-600">
                            {i18n.language === 'fr' ? 'Date d\'échéance :' : 'Due date:'}
                          </p>
                          <p className="font-bold text-blue-900">
                            {getDueDate(formData.paymentTerms)}
                          </p>
                        </div>
                      </div>
                      
                      <p className="text-xs text-blue-700 mt-3">
                        {i18n.language === 'fr' 
                          ? '💡 Une facture sera générée et envoyée par email. Le paiement devra être effectué avant la date d\'échéance.'
                          : '💡 An invoice will be generated and sent by email. Payment must be made before the due date.'
                        }
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Button 
                type="submit" 
                disabled={loading}
                className="w-full py-6 text-lg bg-gradient-to-r from-[#635BFF] to-[#00D4FF] hover:from-[#5046e5] hover:to-[#00b8d9]"
              >
                {loading 
                  ? (i18n.language === 'fr' ? 'Traitement en cours...' : 'Processing...') 
                  : formData.paymentMethod === 'purchase_order'
                    ? (i18n.language === 'fr' ? `✅ Confirmer la commande (paiement à ${formData.paymentTerms} jours)` : `✅ Confirm order (${formData.paymentTerms} days payment)`)
                    : (i18n.language === 'fr' ? '✅ Valider la commande' : '✅ Place Order')
                }
              </Button>
            </form>
          </div>

          {/* Order Summary */}
          <div>
            <Card className="sticky top-24">
              <CardContent className="p-6">
                <h2 className="text-xl font-bold mb-4 text-[#0A2540]">
                  {i18n.language === 'fr' ? 'Récapitulatif' : 'Order Summary'}
                </h2>
                
                <div className="space-y-3 mb-6">
                  {existingOrder ? (
                    // Show items from existing order
                    existingOrder.items?.map((item, index) => (
                      <div key={index} className="flex justify-between text-sm py-2 border-b">
                        <div>
                          <span className="font-medium">{item.product_name || item.name}</span>
                          <span className="text-gray-500 ml-2">x{item.quantity || 1}</span>
                        </div>
                        <span>{(item.total_price || item.price || 0).toLocaleString('fr-FR')} €</span>
                      </div>
                    ))
                  ) : (
                    // Show items from cart
                    cart.map((item, index) => {
                      const price = item.type === 'new' ? item.product.price_new :
                                   item.type === 'used' ? item.product.price_used :
                                   item.product.price_rental;
                      return (
                        <div key={index} className="flex justify-between text-sm py-2 border-b">
                          <div>
                            <span className="font-medium">{i18n.language === 'fr' ? item.product.name_fr : item.product.name_en}</span>
                            <span className="text-gray-500 ml-2">x{item.quantity}</span>
                          </div>
                          <span>{(price * item.quantity).toLocaleString('fr-FR')} €</span>
                        </div>
                      );
                    })
                  )}
                </div>

                <div className="space-y-2 pt-4 border-t">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{i18n.language === 'fr' ? 'Sous-total' : 'Subtotal'}</span>
                    <span>{itemsTotal.toLocaleString('fr-FR')} €</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{i18n.language === 'fr' ? 'Livraison' : 'Delivery'}</span>
                    <span>{deliveryCost.toLocaleString('fr-FR')} €</span>
                  </div>
                  <div className="flex justify-between text-xl font-bold pt-2 border-t">
                    <span>Total</span>
                    <span className="text-[#635BFF]">{total.toLocaleString('fr-FR')} €</span>
                  </div>
                </div>

                {formData.paymentMethod === 'purchase_order' && (
                  <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-sm text-amber-800 font-medium">
                      📅 {i18n.language === 'fr' ? 'Paiement attendu avant le' : 'Payment due by'}:
                    </p>
                    <p className="text-lg font-bold text-amber-900">
                      {getDueDate(formData.paymentTerms)}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
